import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { templateBulkFieldAllowed } from './templateBulkFields';

/** A flagged invoice line item: the row's local `_key` -> its fan-out payload. */
export type InvoiceLineFlags = ReadonlyMap<string, Record<string, unknown>>;

export interface ChangeAllState {
  enabled: boolean;
  targetPros: (number | string)[];
  anchorPro: number | string | null;
  /** The target set is TEMPLATES, not orders. Both boards open the same drawer, and
   *  the two families have different rules about what may be fanned out. */
  isTemplate: boolean;
  /** The ANCHOR template's approval state — what decides whether the State-2 fields
   *  are currently unlocked, and therefore which checkboxes appear. Every target is
   *  re-judged on its own approval by the server, so a mixed set is reported per
   *  record in the review modal rather than silently part-applied. */
  isApproved: boolean;
  /**
   * May a checkbox be drawn for this field? True for every field on an order (the
   * field's own read-only state is handled where it renders); on a template, only for
   * one the policy currently leaves unlocked.
   *
   * Lives here rather than in each checkbox so the ~20 render sites stay unchanged and
   * the rule has one home.
   */
  fieldAllowed: (bulkFieldName: string) => boolean;
  checkedFields: ReadonlySet<string>;
  toggleField: (name: string) => void;
  selectAll: (names: string[]) => void;
  clearAll: () => void;
  checkedLegs: ReadonlySet<string>; // whole-leg selections, keyed by legrecno (as string)
  toggleLeg: (legKey: string) => void;
  /**
   * The two LEG LOCATION fan-out flags (`pickupOrigin` / `terminateDest`).
   *
   * Its own set rather than a corner of `checkedFields`, because the two namespaces
   * resolve differently and would quietly corrupt each other if merged: an entry in
   * `checkedFields` is a view key the order registry must recognise and
   * `buildOrderChangeAllRows` turns into an ORDER column, while these name a leg
   * ROLE that each target pro resolves against its own pointers. Same reason
   * `checkedInvoiceFields` is separate.
   */
  checkedLegFields: ReadonlySet<string>;
  toggleLegField: (name: string) => void;
  /**
   * The Invoicing tab's OWN "apply to all" flags. Its field namespace is view keys
   * (`marginApproval`, `invoiceHoldReason`, …), not the order columns `checkedFields`
   * holds, so the two sets stay separate — but both live HERE, in the provider, rather
   * than in the tab.
   *
   * They used to be InvoicingView's local state, and the tab unmounts whenever the user
   * visits another tab: ticking "apply to all", stepping over to Tracing and back, then
   * saving silently dropped the fan-out — no modal, no error, with the anchor saved. The
   * provider outlives every tab, so the flags now survive the round trip; it's scoped to
   * one pro-drawer session, so opening a different pro still starts clean.
   */
  checkedInvoiceFields: ReadonlySet<string>;
  setCheckedInvoiceFields: React.Dispatch<React.SetStateAction<Set<string>>>;
  checkedInvoiceLines: InvoiceLineFlags;
  setCheckedInvoiceLines: React.Dispatch<
    React.SetStateAction<Map<string, Record<string, unknown>>>
  >;
}

const NOOP_SET_FIELDS: React.Dispatch<React.SetStateAction<Set<string>>> = () => {};
const NOOP_SET_LINES: React.Dispatch<
  React.SetStateAction<Map<string, Record<string, unknown>>>
> = () => {};

const DISABLED: ChangeAllState = {
  enabled: false,
  targetPros: [],
  anchorPro: null,
  isTemplate: false,
  isApproved: false,
  fieldAllowed: () => true,
  checkedFields: new Set(),
  toggleField: () => {},
  selectAll: () => {},
  clearAll: () => {},
  checkedLegs: new Set(),
  toggleLeg: () => {},
  checkedLegFields: new Set(),
  toggleLegField: () => {},
  checkedInvoiceFields: new Set(),
  setCheckedInvoiceFields: NOOP_SET_FIELDS,
  checkedInvoiceLines: new Map(),
  setCheckedInvoiceLines: NOOP_SET_LINES,
};

const Ctx = createContext<ChangeAllState>(DISABLED);

export const useChangeAll = (): ChangeAllState => useContext(Ctx);

export const ChangeAllProvider: React.FC<{
  enabled: boolean;
  targetPros: (number | string)[];
  anchorPro: number | string | null;
  /** Anchor is a template ("T…"), so the template field policy decides which
   *  checkboxes exist. Defaults false — orders behave exactly as before. */
  isTemplate?: boolean;
  /** Anchor template's saved approval state (State-2 fields lock once approved). */
  isApproved?: boolean;
  children: React.ReactNode;
  /** Optional ref kept in sync with the live context value, so a component that
   *  PROVIDES this context (e.g. ProDetailContainer.handleSave) can read the
   *  current checkedFields/targetPros without calling useChangeAll(). */
  stateRef?: React.MutableRefObject<ChangeAllState | null>;
  /** Called with the live value whenever it changes. The ref above is enough to
   *  READ state inside an event handler, but a provider that must RE-RENDER on a
   *  flag change needs a real subscription — the footer's Save button enables on
   *  a flagged field even when the anchor itself has no edits. */
  onStateChange?: (state: ChangeAllState) => void;
}> = ({
  enabled, targetPros, anchorPro, isTemplate = false, isApproved = false,
  children, stateRef, onStateChange,
}) => {
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [checkedLegs, setCheckedLegs] = useState<Set<string>>(new Set());
  const [checkedLegFields, setCheckedLegFields] = useState<Set<string>>(new Set());
  const [checkedInvoiceFields, setCheckedInvoiceFields] = useState<Set<string>>(new Set());
  const [checkedInvoiceLines, setCheckedInvoiceLines] = useState<
    Map<string, Record<string, unknown>>
  >(new Map());
  const toggleField = useCallback((name: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);
  const toggleLeg = useCallback((legKey: string) => {
    setCheckedLegs((prev) => {
      const next = new Set(prev);
      if (next.has(legKey)) next.delete(legKey);
      else next.add(legKey);
      return next;
    });
  }, []);
  const toggleLegField = useCallback((name: string) => {
    setCheckedLegFields((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);
  // `selectAll` is the banner's "flag all edited" sweep over ORDER fields. It does
  // not reach the leg flags on purpose: a leg location is a physical stop, and the
  // one thing a sweep must never do is pick it up as a side effect of flagging
  // everything else. The two leg checkboxes are only ever ticked by hand.
  const selectAll = useCallback((names: string[]) => setChecked(new Set(names)), []);
  // The banner's Clear button — it clears every flag the banner counts, which now
  // includes the Invoicing tab's.
  const clearAll = useCallback(() => {
    setChecked(new Set());
    setCheckedLegs(new Set());
    setCheckedLegFields(new Set());
    setCheckedInvoiceFields(new Set());
    setCheckedInvoiceLines(new Map());
  }, []);

  const fieldAllowed = useCallback(
    (bulkFieldName: string) => (
      isTemplate ? templateBulkFieldAllowed(bulkFieldName, { isApproved }) : true
    ),
    [isTemplate, isApproved],
  );

  const value = useMemo<ChangeAllState>(
    () => ({
      enabled, targetPros, anchorPro, isTemplate, isApproved, fieldAllowed,
      checkedFields: checked, toggleField, selectAll,
      clearAll, checkedLegs, toggleLeg, checkedLegFields, toggleLegField,
      checkedInvoiceFields, setCheckedInvoiceFields,
      checkedInvoiceLines, setCheckedInvoiceLines,
    }),
    [
      enabled, targetPros, anchorPro, isTemplate, isApproved, fieldAllowed,
      checked, toggleField, selectAll, clearAll,
      checkedLegs, toggleLeg, checkedLegFields, toggleLegField,
      checkedInvoiceFields, checkedInvoiceLines,
    ],
  );
  useEffect(() => {
    if (stateRef) stateRef.current = value;
    onStateChange?.(value);
  }, [stateRef, onStateChange, value]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
